import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Film, Eye, Camera, Gift, MailCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("viewer");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referral, setReferral] = useState(null); // { referral_code, filmmaker_name, credit_naira }
  const [submittedEmail, setSubmittedEmail] = useState(""); // post-signup state
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const code = localStorage.getItem("cv_referral");
    if (code) {
      api.get(`/referrals/lookup/${code}`).then((r) => setReferral(r.data)).catch(() => {
        localStorage.removeItem("cv_referral");
      });
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (role === "filmmaker" && !phoneNumber.trim()) {
      toast.error("Phone number is required for filmmakers");
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the Terms and Conditions");
      return;
    }
    setLoading(true);
    try {
      const payload = { name, email, password, role, accept_terms: true };
      if (phoneNumber.trim()) payload.phone_number = phoneNumber.trim();
      if (referral?.referral_code) payload.referral_code = referral.referral_code;
      // Hit the register endpoint directly — we don't go through AuthContext
      // because the server now requires email verification before issuing a
      // session, so there's no user to set on success.
      await api.post("/auth/register", payload);
      localStorage.removeItem("cv_referral");
      setSubmittedEmail(email);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResendLoading(true);
    try {
      await api.post("/auth/resend-verification", { email: submittedEmail });
      toast.success("Fresh confirmation link sent. Check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend confirmation email");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink grain flex items-center justify-center px-4 py-16">
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center group">
          <Logo size={32} />
        </Link>
      </div>

      <div className="w-full max-w-md bg-surface border border-white/10 rounded-2xl p-8 sm:p-10 shadow-2xl shadow-terracotta/5" data-testid="register-card">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-black mb-2">
            {submittedEmail ? "Check your inbox" : "Create your account"}
          </h1>
          <p className="text-foreground/60 text-sm">
            {submittedEmail
              ? "One last step before you start streaming."
              : "Watch shorts or upload your own. Free to join."}
          </p>
        </div>

        {submittedEmail ? (
          <div className="space-y-5" data-testid="register-check-inbox">
            <div className="p-4 rounded-lg border border-gold/30 bg-gold/10 flex items-start gap-3">
              <MailCheck className="w-6 h-6 text-gold shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-display font-bold text-gold">Confirmation link sent</p>
                <p className="text-foreground/75 mt-1">
                  We sent a one-time link to <span className="text-foreground font-semibold">{submittedEmail}</span>.
                  Click it within 24 hours to activate your account and log in automatically.
                </p>
              </div>
            </div>
            <div className="text-xs text-foreground/55 space-y-1.5 px-1">
              <p>· Not seeing it? Check spam/promotions.</p>
              <p>· The link expires in 24 hours and can only be used once.</p>
              <p>· Wrong email? You can sign up again with the correct one.</p>
            </div>
            <Button
              type="button"
              onClick={resendVerification}
              disabled={resendLoading}
              variant="outline"
              className="w-full border-white/15 bg-transparent hover:bg-white/5 h-11"
              data-testid="register-resend-btn"
            >
              {resendLoading ? "Sending…" : "Resend confirmation email"}
            </Button>
            <Button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full bg-terracotta hover:bg-ember text-white h-11"
              data-testid="register-go-to-login-btn"
            >
              Back to sign in
            </Button>
          </div>
        ) : (
        <>
        {referral && (
          <div className="mb-6 p-3 rounded-lg border border-gold/30 bg-gold/10 flex items-start gap-3" data-testid="referral-banner">
            <Gift className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-display font-bold text-gold">₦{referral.credit_naira} off your first rental</p>
              <p className="text-foreground/70 text-xs">Referred by {referral.filmmaker_name}</p>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-foreground/80 text-sm">I am a…</Label>
            <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3 mt-2" data-testid="register-role-radio">
              <Label
                htmlFor="r-viewer"
                className={`cursor-pointer p-4 rounded-lg border transition-all ${role === "viewer" ? "border-terracotta bg-terracotta/10" : "border-white/10 bg-ink hover:border-white/20"}`}
                data-testid="role-viewer-option"
              >
                <RadioGroupItem id="r-viewer" value="viewer" className="sr-only" />
                <Eye className="w-5 h-5 mb-1.5 text-terracotta" />
                <p className="font-display font-bold">Viewer</p>
                <p className="text-xs text-foreground/60">Discover & rent shorts</p>
              </Label>
              <Label
                htmlFor="r-fm"
                className={`cursor-pointer p-4 rounded-lg border transition-all ${role === "filmmaker" ? "border-terracotta bg-terracotta/10" : "border-white/10 bg-ink hover:border-white/20"}`}
                data-testid="role-filmmaker-option"
              >
                <RadioGroupItem id="r-fm" value="filmmaker" className="sr-only" />
                <Camera className="w-5 h-5 mb-1.5 text-gold" />
                <p className="font-display font-bold">Filmmaker</p>
                <p className="text-xs text-foreground/60">Upload & monetize</p>
              </Label>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="name" className="text-foreground/80 text-sm">Full name</Label>
            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email" className="text-foreground/80 text-sm">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password" className="text-foreground/80 text-sm">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-ink border-white/10 h-11" data-testid="register-password-input" />
            <p className="text-xs text-foreground/40 mt-1">At least 6 characters</p>
          </div>
          <div>
            <Label htmlFor="phone" className="text-foreground/80 text-sm">
              Phone number{role === "filmmaker" ? "" : <span className="text-foreground/40"> (optional)</span>}
            </Label>
            <Input
              id="phone"
              type="tel"
              required={role === "filmmaker"}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+234 800 123 4567 or +1 415 555 0123"
              className="mt-1.5 bg-ink border-white/10 h-11"
              data-testid="register-phone-input"
            />
            <p className="text-xs text-foreground/40 mt-1">
              Include your country code (e.g. +234, +1, +44).{" "}
              {role === "filmmaker"
                ? "Required for payout coordination."
                : "Helps with account recovery."}{" "}
              Not shown publicly.
            </p>
          </div>
          <div className="flex items-start gap-2.5" data-testid="register-terms-row">
            <Checkbox id="accept-terms" checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" data-testid="register-terms-checkbox" />
            <label htmlFor="accept-terms" className="text-xs text-foreground/65 leading-relaxed">
              By creating an account you agree to the{" "}
              <Link to="/terms" target="_blank" rel="noopener" className="text-terracotta hover:underline" data-testid="register-terms-link">Terms and Conditions</Link>
              {" "}and{" "}
              <Link to="/privacy-policy" target="_blank" rel="noopener" className="text-terracotta hover:underline">Privacy Policy</Link>.
            </label>
          </div>
          <Button type="submit" disabled={loading || !acceptTerms} className="w-full bg-terracotta hover:bg-ember text-white h-11" data-testid="register-submit-btn">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-center text-foreground/60">
          Already have an account?{" "}
          <Link to="/login" className="text-terracotta hover:text-ember" data-testid="link-to-login">
            Sign in
          </Link>
        </p>
        </>
        )}
      </div>
    </div>
  );
}
